"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CAChart, MargeGauge, StatCounters } from "@/components/dashboard/dashboard-charts";
import {
  TopClientsChart,
  ClientsRelanceList,
  ProspectsEnCoursList,
} from "@/components/dashboard/dashboard-lists";
import { ActionsSummaryCard } from "@/components/dashboard/actions-summary-card";
import { TodayActions } from "@/components/dashboard/today-actions";
import { ActivityTimeline } from "@/components/dashboard/activity-timeline";
import { DashboardInsights } from "@/components/ai/DashboardInsights";
import { useDashboardData, type DashboardKPI } from "@/hooks/use-dashboard-data";
import { useAuth } from "@/providers/auth-provider";
import { cn } from "@/lib/utils";

interface DoliUser {
  id: string;
  login: string;
  firstname: string;
  lastname: string;
  statut: string;
}

// ── KPI Card ──
function KPICard({
  kpi,
  delay,
}: {
  kpi: DashboardKPI;
  delay: number;
}) {
  const inner = (
    <Card
      className={cn(
        "overflow-hidden transition-all hover:shadow-md",
        kpi.href && "cursor-pointer"
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p
              className="text-2xl font-bold tabular-nums"
              style={{ color: kpi.color }}
            >
              {kpi.value}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {kpi.label}
            </p>
          </div>
          {kpi.subtext && (
            <span className="text-[10px] text-muted-foreground/60">
              {kpi.subtext}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
    >
      {kpi.href ? <Link href={kpi.href}>{inner}</Link> : inner}
    </motion.div>
  );
}

// ── User Filter (Select dropdown) ──
function UserFilter({
  users,
  selectedUser,
  onSelect,
}: {
  users: DoliUser[];
  selectedUser: number | null;
  onSelect: (id: number | null) => void;
}) {
  const activeUsers = users.filter((u) => u.statut === "1");
  if (activeUsers.length === 0) return null;

  return (
    <Select
      value={selectedUser === null ? "all" : String(selectedUser)}
      onValueChange={(v) => onSelect(v === "all" ? null : Number(v))}
    >
      <SelectTrigger className="w-[180px] h-8 text-xs">
        <SelectValue placeholder="Vendeur" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Tous</SelectItem>
        {activeUsers.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            {u.firstname || u.login}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Loading skeleton ──
function DashboardSkeleton() {
  return (
    <div className="space-y-6 p-1">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} className="h-[76px] rounded-xl" />
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Skeleton className="h-[220px] rounded-xl" />
        <Skeleton className="h-[220px] rounded-xl" />
        <Skeleton className="h-[220px] rounded-xl" />
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Skeleton className="h-[220px] rounded-xl" />
        <Skeleton className="h-[220px] rounded-xl" />
        <Skeleton className="h-[220px] rounded-xl" />
      </div>
    </div>
  );
}

// ── Main ──
export default function DashboardPage() {
  const { user, client } = useAuth();
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [users, setUsers] = useState<DoliUser[]>([]);
  const { data, isLoading } = useDashboardData(selectedUser);

  const firstName = user?.name?.split(" ")[0] || "Utilisateur";

  useEffect(() => {
    if (!client) return;
    client.getUsers({ limit: 50 }).then(setUsers).catch(() => {});
  }, [client]);

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
            Bonjour, {firstName}
          </h1>
          <p className="text-muted-foreground">Chargement du tableau de bord...</p>
        </div>
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + User Filter */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
          Bonjour, {firstName}
        </h1>
        <p className="text-muted-foreground">Tableau de bord commercial</p>
        <div className="mt-2">
          <UserFilter
            users={users}
            selectedUser={selectedUser}
            onSelect={setSelectedUser}
          />
        </div>
      </motion.div>

      {/* LIGNE 1: KPI Cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {data.kpis.map((kpi, i) => (
          <KPICard key={kpi.label} kpi={kpi} delay={i * 0.04} />
        ))}
      </div>

      {/* LIGNE 2: Charts */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="grid gap-4 md:grid-cols-3"
      >
        <CAChart data={data.caMonthly} />
        <MargeGauge
          margePercent={data.marge.percent}
          margeEuros={data.marge.euros}
        />
        <StatCounters
          acceptes={data.counters.acceptes}
          factures={data.counters.factures}
        />
      </motion.div>

      {/* LIGNE 3: Top Listes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="grid gap-4 md:grid-cols-3"
      >
        <TopClientsChart data={data.topClients} />
        <ClientsRelanceList data={data.relances} />
        <ProspectsEnCoursList data={data.prospects} />
      </motion.div>

      {/* LIGNE 4: Actions du jour + Activité récente */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid gap-4 md:grid-cols-2"
      >
        <TodayActions />
        <ActivityTimeline />
      </motion.div>

      {/* LIGNE 5: Insights IA */}
      <DashboardInsights dashboardData={data} />

      {/* LIGNE 6: Actions résumé (détail → /crm/actions) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <ActionsSummaryCard />
      </motion.div>
    </div>
  );
}
